import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useRef } from 'react';
import { Play, Check } from 'lucide-react';
import logoHostVslWhite from '@/assets/logo-hostvsl-white.png';
import { PlaylistVideo } from '@/store/playlist/types';
import logoHostVSL from '@/assets/logo-hostvsl-white.png';
import { TemplateLightFooter } from './TemplateLightFooter';

interface TemplateCleanPreviewProps {
  videos: PlaylistVideo[];
  playlistName: string;
  publicTitle?: string;
  description?: string;
  favicon?: string;
}

export function TemplateCleanPreview({ 
  videos, 
  playlistName, 
  publicTitle, 
  description, 
  favicon 
}: TemplateCleanPreviewProps) {
  useLanguage();
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(videos[0]?.id || null);
  const [loadingCards, setLoadingCards] = useState(true);
  const [showNextOverlay, setShowNextOverlay] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const playerRef = useRef<HTMLDivElement>(null);

  // Encontrar o vídeo selecionado
  const selectedVideo = videos.find(v => v.id === selectedVideoId);
  const selectedVideoIndex = videos.findIndex(v => v.id === selectedVideoId);
  
  // Próximo vídeo
  const nextVideo = selectedVideoIndex < videos.length - 1 ? videos[selectedVideoIndex + 1] : null;
  const nextVideoIndex = selectedVideoIndex + 1;

  // Simular loading dos cards
  useEffect(() => {
    const timer = setTimeout(() => setLoadingCards(false), 800);
    return () => clearTimeout(timer);
  }, []);
  
  // Countdown timer para autoplay
  useEffect(() => {
    if (!showNextOverlay) return;
    
    if (countdown <= 0) {
      goToNextVideo();
      return;
    }
    
    const timer = setTimeout(() => {
      setCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [showNextOverlay, countdown]);
  
  // Função para avançar ao próximo vídeo
  const goToNextVideo = () => {
    if (nextVideo) {
      setSelectedVideoId(nextVideo.id);
    }
    setShowNextOverlay(false);
    setCountdown(5);
  };
  
  // Handler quando vídeo termina
  const handleVideoEnded = () => {
    if (nextVideo) {
      setShowNextOverlay(true);
    }
  };
  
  // Cancelar autoplay
  const cancelAutoplay = () => {
    setShowNextOverlay(false);
    setCountdown(5);
  };

  // Handler para selecionar vídeo e fazer scroll
  const handleSelectVideo = (videoId: string) => {
    setSelectedVideoId(videoId);
    playerRef.current?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div data-theme-isolated className="min-h-screen bg-white flex flex-col overflow-y-auto">
      {/* Header Premium */}
      <header 
        className="h-[64px] flex items-center px-5 sticky top-0 z-50"
        style={{ 
          backgroundColor: '#0059FF',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
        }}
      >
        <img 
          src={favicon || logoHostVSL} 
          alt={translateInterfaceText("Logo")}
          className="h-6 object-contain"
        />
      </header>

      {/* Player Reduzido e Centralizado */}
      <section ref={playerRef} className="py-4 md:py-10 px-4">
        <div 
          className="mx-auto relative bg-[#0B1423] overflow-hidden aspect-video"
          style={{
            maxWidth: '860px',
            maxHeight: '480px',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
          }}
        >
          {selectedVideo ? (
            <>
              <video
                key={selectedVideo.id}
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
                src={`https://example.com/videos/${selectedVideo.videoId}.mp4`}
                onEnded={handleVideoEnded}
              >
                {translateInterfaceText("Seu navegador não suporta a reprodução de vídeo.")}</video>
              
              {/* Overlay de Próximo Vídeo */}
              {showNextOverlay && nextVideo && (
                <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center z-10 backdrop-blur-sm">
                  {/* Info do próximo vídeo */}
                  <p className="text-white/60 text-sm mb-2">{translateInterfaceText("Próximo episódio em")}</p>
                  
                  {/* Countdown circular */}
                  <div className="relative w-20 h-20 mb-4">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50" cy="50" r="45"
                        stroke="white" strokeWidth="3" fill="none" opacity="0.2"
                      />
                      <circle
                        cx="50" cy="50" r="45"
                        stroke="#0059FF" strokeWidth="3" fill="none"
                        strokeDasharray={`${2 * Math.PI * 45}`}
                        strokeDashoffset={`${2 * Math.PI * 45 * (1 - countdown / 5)}`}
                        className="transition-all duration-1000 ease-linear"
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-white text-3xl font-bold">
                      {countdown}
                    </span>
                  </div>
                  
                  {/* Thumbnail + título do próximo */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-24 h-14 bg-[#0B1423] rounded-lg flex items-center justify-center">
                      <img src={logoHostVslWhite} alt="" className="w-12 opacity-60" />
                    </div>
                    <div className="text-left">
                      <p className="text-white/60 text-xs">{translateInterfaceText("Episódio")}{" "}{nextVideoIndex + 1}</p>
                      <p className="text-white font-semibold text-sm">{nextVideo.episodeTitle}</p>
                    </div>
                  </div>
                  
                  {/* Botões */}
                  <div className="flex gap-3">
                    <button
                      onClick={cancelAutoplay}
                      className="px-6 py-2.5 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
                    >
                      {translateInterfaceText("Cancelar")}</button>
                    <button
                      onClick={goToNextVideo}
                      className="px-6 py-2.5 rounded-full bg-[#0059FF] text-white text-sm font-medium hover:bg-[#0047CC] transition-colors"
                    >
                      {translateInterfaceText("Reproduzir agora")}</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#0059FF' }}
              >
                <Play className="w-10 h-10 text-white fill-white" />
              </div>
            </div>
          )}
        </div>
        
        {/* Título do vídeo sendo reproduzido */}
        {selectedVideo && (
          <div className="text-center mt-4 mx-auto" style={{ maxWidth: '860px' }}>
            <p className="text-[#1A1A1A] font-semibold text-base md:text-lg">
              {translateInterfaceText("Episódio")}{" "}{selectedVideoIndex + 1}: {selectedVideo.episodeTitle}
            </p>
          </div>
        )}
      </section>

      {/* Título + Descrição do Curso */}
      <section className="text-center px-4 mb-4 md:mb-10">
        <h1 
          className="font-bold text-[#1A1A1A] mx-auto leading-tight text-xl md:text-[28px]"
          style={{ fontWeight: 700, maxWidth: '860px' }}
        >
          {publicTitle || playlistName}
        </h1>
        
        {description && (
          <p 
            className="text-[#6B7280] mx-auto mt-3 leading-relaxed"
            style={{ fontSize: '16px', maxWidth: '860px' }}
          >
            {description}
          </p>
        )}
      </section>

      {/* Grid de Vídeos (Estilo Panda) */}
      <section className="pb-20 md:pb-16 flex-1">
        {/* Mobile: Slider Horizontal */}
        <div className="md:hidden px-4">
          <div 
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4"
            style={{ 
              scrollBehavior: 'smooth',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <style>{`
              .overflow-x-auto::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {loadingCards ? (
              // Loading Skeletons
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 snap-start" style={{ width: '75vw', maxWidth: '280px' }}>
                  <VideoCardSkeleton />
                </div>
              ))
            ) : (
              // Video Cards
              videos.map((video, index) => (
                <div 
                  key={video.id}
                  className="flex-shrink-0 snap-start"
                  style={{ width: '75vw', maxWidth: '280px' }}
                >
                  <VideoCard 
                    video={video}
                    index={index}
                    isSelected={selectedVideoId === video.id}
                    onClick={() => handleSelectVideo(video.id)}
                    formatDuration={formatDuration}
                    progress={index === 0 ? 75 : index === 1 ? 100 : index === 2 ? 30 : 0}
                    isWatched={index === 1}
                  />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Desktop/Tablet: Grid */}
        <div className="hidden md:block px-4">
          <div 
            className="mx-auto grid grid-cols-2 lg:grid-cols-4"
            style={{ 
              maxWidth: '1400px',
              gap: '32px 24px'
            }}
          >
            {loadingCards ? (
              // Loading Skeletons
              Array.from({ length: 8 }).map((_, i) => (
                <VideoCardSkeleton key={i} />
              ))
            ) : (
              // Video Cards
              videos.map((video, index) => (
                <VideoCard 
                  key={video.id}
                  video={video}
                  index={index}
                  isSelected={selectedVideoId === video.id}
                  onClick={() => handleSelectVideo(video.id)}
                  formatDuration={formatDuration}
                  progress={index === 0 ? 75 : index === 1 ? 100 : index === 2 ? 30 : 0}
                  isWatched={index === 1}
                />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Rodapé Premium - Mesmo padrão do template Panda */}
      <TemplateLightFooter 
        showTerms={true}
        showPrivacy={true}
        showWhatsapp={true}
        whatsappNumber="11999999999"
        whatsappMessage="Olá! Vim pela playlist"
      />
    </div>
  );
}

// Video Card Component com Microinterações Premium
interface VideoCardProps {
  video: PlaylistVideo;
  index: number;
  isSelected: boolean;
  onClick: () => void;
  formatDuration: (seconds?: number) => string;
  progress?: number;
  isWatched?: boolean;
}

function VideoCard({ video, index, isSelected, onClick, formatDuration, progress = 0, isWatched = false }: VideoCardProps) {
  useLanguage();
  return (
    <div 
      className="cursor-pointer"
      onClick={onClick}
    >
      {/* Thumbnail 16:9 */}
              <div 
                className="relative aspect-video overflow-hidden mb-3"
                style={{
                  backgroundColor: '#0B1423',
                  borderRadius: '12px',
                  border: isSelected ? '2px solid #0059FF' : 'none',
                }}
              >
                {/* Logo HostVSL Centralizada */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <img 
                    src={logoHostVslWhite} 
                    alt={translateInterfaceText("HostVSL")}
                    className="w-24 h-auto opacity-60"
                  />
                </div>

        {/* Barra de Progresso Visual */}
        {progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-black/20">
            <div 
              className="h-full bg-[#3B82F6] transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        )}

        {/* Badge de Concluído */}
        {isWatched && (
          <div className="absolute top-2 left-2 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}

        {/* Selected Indicator */}
        {isSelected && (
          <div 
            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#0059FF' }}
          >
            <Play className="w-3 h-3 text-white fill-white" />
          </div>
        )}
      </div>
      
      {/* Número do Episódio */}
      <p 
        className="text-[#0059FF] font-semibold mb-1"
        style={{ fontSize: '12px' }}
      >
        {translateInterfaceText("Episódio")}{" "}{index + 1}
      </p>

      {/* Título */}
      <h3 
        className="font-bold text-[#1A1A1A] line-clamp-2 leading-snug"
        style={{ fontSize: '15px' }}
      >
        {video.episodeTitle || translateInterfaceText("Vídeo {value0}", { value0: (index + 1) })}
      </h3>
      
      {/* Tempo */}
      <p 
        className="text-[#6B7280] mt-1"
        style={{ fontSize: '13px' }}
      >
        {formatDuration(Math.floor(Math.random() * 600 + 180))}
      </p>
    </div>
  );
}

// Loading Skeleton Component
function VideoCardSkeleton() {
  useLanguage();
  return (
    <div className="animate-pulse">
      <div 
        className="aspect-video mb-3"
        style={{
          backgroundColor: '#E5E7EB',
          borderRadius: '12px',
          background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
          backgroundSize: '400% 100%',
          animation: 'shimmer 2s infinite'
        }}
      />
      <div 
        className="h-4 rounded mb-2"
        style={{
          backgroundColor: '#E5E7EB',
          width: '75%'
        }}
      />
      <div 
        className="h-3 rounded"
        style={{
          backgroundColor: '#E5E7EB',
          width: '25%'
        }}
      />
      
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
